/* cf-blade-hd-hinges.js — Click Fencing
 *
 * Pete, 2026-08-25: "add heavy duty hinges for blade gates at 1500mm and 1800mm. make
 * the hd hinges the auto choice for those blade types."
 *
 * What is already there:
 *   blade.js knows about the heavy duty pair. generateHingeTypeOptions() carries
 *   HD_SKU = "TCHD1AL2S3BT" and offers it when fenceHeight >= 1500, the quote builder
 *   prices it (CF_PRICE, falling back to 8010c) and labels it "TruClose Heavy Duty",
 *   and the cart takes its variant id from CF_VARIANT. Measured on the preview theme,
 *   CF_VARIANT("TCHD1AL2S3BT") resolves to 43148003147870 at $88.11, so the product and
 *   the plumbing are both live.
 *
 * What is broken:
 *   The hinge dropdown is generated ONCE, when the gate form is first rendered, and it
 *   is passed the fence height as it was at that moment - normally 1200, the default.
 *   Changing the height afterwards never regenerates it. Measured at 1200, 1500 and
 *   1800 on the preview theme: the dropdown held "Adjustable Tension" and "Soft Close
 *   Premium" at every height and never offered the heavy duty pair. The feature exists
 *   and never fires.
 *
 * What this does:
 *   1. Keeps the hinge dropdown in step with the fence height - adds the heavy duty
 *      option at 1500 and 1800, removes it below that, which is blade.js's own rule.
 *   2. Makes heavy duty the SELECTED hinge at those heights, which is the new part.
 *
 * It stands down as soon as the customer picks a hinge themselves - any change on the
 * dropdown that this file did not cause. After that it only keeps the option list right
 * and never touches their choice again.
 *
 * Blade only. Flat top and PerfMesh carry no heavy duty SKU at all, so there is nothing
 * to select there and this does nothing on those pages.
 *
 * Safety: it only ever changes which hinge is selected, never a price, a quantity or a
 * panel. Everything downstream - the breakdown line, the variant id, the cart - is
 * blade.js's own code reading its own select, so there is no second source of truth to
 * drift. Every entry point is in try/catch.
 */
(function () {
  'use strict';
  if (window.__cfBladeHdHingesInstalled) return;
  window.__cfBladeHdHingesInstalled = 1;

  var LOG = '[cf-blade-hd-hinges]';
  var HD_SKU = 'TCHD1AL2S3BT';
  var HD_LABEL = 'TruClose Heavy Duty (Black)';
  var MIN_HEIGHT = 1500;
  var FALLBACK = 'SHG-90L';

  var chosenByHand = {};      // sectionNum -> the customer has picked a hinge
  var pending = null;

  function heightFor(n) {
    var el = document.getElementById('section-' + n + '-fence-height') ||
             document.getElementById('shared-height');
    return el ? (parseInt(el.value, 10) || 0) : 0;
  }

  function hdIsReal() {
    try { return !!(window.CF_VARIANT && window.CF_VARIANT(HD_SKU)); } catch (e) { return false; }
  }

  function optionFor(sel, value) {
    for (var i = 0; i < sel.options.length; i++) {
      if (sel.options[i].value === value) return sel.options[i];
    }
    return null;
  }

  /* Set the value the way the calculator's own listener expects to see it.
     `force` matters when we have just removed the selected option: the browser silently
     moves the select to its first option, so the value string already reads the way we
     want it and a plain equality check would skip the change event. Measured on the
     preview theme - the select showed Adjustable Tension while the quote still carried
     the heavy duty pair at $88.11, because no change event ever fired. */
  var weAreSetting = false;

  function setValue(sel, value, force) {
    if (!force && sel.value === value) return false;
    weAreSetting = true;
    try {
      sel.value = value;
      sel.dispatchEvent(new Event('change', { bubbles: true }));
    } finally {
      weAreSetting = false;
    }
    return true;
  }

  function sync() {
    var sels = document.querySelectorAll('select[id$="-hinge-color"]');
    for (var s = 0; s < sels.length; s++) {
      var sel = sels[s];
      var m = /section-(\d+)-hinge-color/.exec(sel.id || '');
      if (!m) continue;
      var n = m[1];
      var eligible = heightFor(n) >= MIN_HEIGHT && hdIsReal();
      var opt = optionFor(sel, HD_SKU);

      if (eligible && !opt) {
        opt = document.createElement('option');
        opt.value = HD_SKU;
        opt.textContent = HD_LABEL;
        sel.appendChild(opt);
      } else if (!eligible && opt) {
        var wasSelected = sel.value === HD_SKU;
        opt.parentNode.removeChild(opt);
        if (wasSelected) {
          setValue(sel, optionFor(sel, FALLBACK) ? FALLBACK :
                        (sel.options.length ? sel.options[0].value : ''), true);
          try { console.log(LOG, 'section', n, 'back under', MIN_HEIGHT + 'mm — heavy duty withdrawn'); } catch (e) {}
        }
        continue;
      }

      if (eligible && !chosenByHand[n] && setValue(sel, HD_SKU, false)) {
        try { console.log(LOG, 'section', n, 'at', heightFor(n) + 'mm — heavy duty selected'); } catch (e) {}
      }
    }
  }

  function tick() {
    if (pending) return;
    pending = setTimeout(function () {
      pending = null;
      try { sync(); } catch (e) { try { console.warn(LOG, e); } catch (e2) {} }
    }, 250);
  }

  /* A change on the dropdown that did not come from us is the customer's decision, and
     it outranks ours. This deliberately does NOT test event.isTrusted: that is false for
     any scripted selection, which would include a real browser automation and, more to
     the point, made this untestable - the harness picked Soft Close and was silently
     overridden. The flag is set only around our own dispatch, and change listeners run
     synchronously inside dispatchEvent, so it cannot be stale. */
  document.addEventListener('change', function (e) {
    try {
      var t = e.target;
      if (!t || !t.id) return;
      if (/-hinge-color$/.test(t.id)) {
        if (!weAreSetting) {
          var m = /section-(\d+)-hinge-color/.exec(t.id);
          if (m) chosenByHand[m[1]] = true;
        }
        return;
      }
      if (/-fence-height$/.test(t.id) || t.id === 'shared-height') tick();
    } catch (err) {}
  }, true);

  function start() {
    tick();
    try {
      new MutationObserver(tick).observe(document.body, { childList: true, subtree: true });
    } catch (e) {}
    try { console.log(LOG, 'installed'); } catch (e) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
